/**
 * Cart nudge email safety (Admin Cart Reports + future automation).
 *
 * Opt-in only: set REACT_APP_CART_NUDGE_TEST_MODE=true so **no** cart nudge is delivered
 * to real customer inboxes — everything routes to a test inbox with a visible banner.
 *
 * Does not affect order confirmations or other mail unless those paths import this module.
 */

function escapeHtmlAttr(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * When true, all cart nudge sends are redirected to the test inbox (see applyCartNudgeTestRouting).
 * Production default: false (unset).
 */
export function isCartNudgeTestModeEnabled() {
    return String(process.env.REACT_APP_CART_NUDGE_TEST_MODE || '').toLowerCase() === 'true';
}

/**
 * Inbox used when test mode is on. Falls back to global email test address then digital.
 */
export function getCartNudgeTestInboxEmail() {
    const raw =
        process.env.REACT_APP_CART_NUDGE_TEST_EMAIL ||
        process.env.REACT_APP_EMAIL_TEST_ADDRESS ||
        'digital@londonsmokeandcure.co.uk';
    return String(raw).trim();
}

/**
 * Future: gate scheduled/automated nudges — must be explicitly enabled in env.
 */
export function isCartNudgeAutomationEnabled() {
    return String(process.env.REACT_APP_CART_NUDGE_AUTOMATION_ENABLED || '').toLowerCase() === 'true';
}

/**
 * @param {Object} p
 * @param {string|string[]} p.to - intended customer recipient(s)
 * @param {string} [p.subject]
 * @param {string} [p.text]
 * @param {string} [p.html]
 * @returns {{ to: string, subject: string, text: string, html: string, isTestMode: boolean, bypassTestMode: boolean, intendedRecipient: string }}
 */
export function applyCartNudgeTestRouting({ to, subject = '', text = '', html = '' }) {
    const intended = Array.isArray(to) ? to.filter(Boolean).join(', ') : String(to || '').trim();

    if (!isCartNudgeTestModeEnabled()) {
        return {
            to,
            subject,
            text,
            html,
            isTestMode: false,
            bypassTestMode: false,
            intendedRecipient: intended,
        };
    }

    const inbox = getCartNudgeTestInboxEmail();
    const safeIntended = escapeHtmlAttr(intended);
    const textBanner = `[CART NUDGE TEST MODE — intended recipient: ${intended}]\n\n`;
    const htmlBanner =
        `<p style="margin:0 0 14px 0;padding:10px 12px;background:#fef3c7;border:1px solid #d97706;border-radius:8px;font-size:13px;color:#78350f;line-height:1.45;">` +
        `<strong>TEST MODE</strong> — This cart nudge would be sent to <strong>${safeIntended}</strong>. No customer inbox receives this while <code style="font-size:12px;">REACT_APP_CART_NUDGE_TEST_MODE</code> is true.` +
        `</p>`;

    let subj = subject || '';
    if (!/^\[TEST\]/i.test(subj.trim())) {
        subj = `[TEST] ${subj}`.trim();
    }

    return {
        to: inbox,
        subject: subj,
        text: textBanner + text,
        html: htmlBanner + (html || ''),
        isTestMode: true,
        bypassTestMode: true,
        intendedRecipient: intended,
    };
}
